import { httpClient } from './http-client';
import { API_ENDPOINTS } from '../config/api';

export type AuctionType = 'Offline' | 'Online' | 'Hybrid';
export type AuctionStatus = 'Upcoming' | 'Active' | 'Completed';
export type LotBiddingState = 'NotScheduled' | 'NotStarted' | 'Open' | 'Closed';
export type LotResult = 'Pending' | 'Sold' | 'NoBids';
export type ParticipantStatus = 'Pending' | 'Approved' | 'Rejected' | 'Blocked';

export interface AuctionLotItem {
  id: number;
  auctionId: number;
  lotNumber: number;
  name: string;
  inj: string;
  birthDate?: string | null;
  ageMonths?: number | null;
  birthWeight?: number | null;
  weaningWeight?: number | null;
  yearlingWeight?: number | null;
  currentWeight?: number | null;
  averageDailyGain?: number | null;
  scrotalCircumference?: number | null;
  spermQualityScore?: number | null;
  heightAtWithers?: number | null;
  line?: string | null;
  startingPrice: number;
  image?: string | null;
  imageUrl?: string | null;
  /** Своё время закрытия лота (UTC ISO); null — закрывается с окончанием аукциона */
  closesAt?: string | null;
  biddingClosesAt?: string | null;
  biddingState: LotBiddingState;
  currentPrice?: number | null;
  minNextBid: number;
  bidsCount: number;
  result: LotResult;
  winnerNumber?: number | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface AuctionListItem {
  id: number;
  titleKz: string;
  titleRu: string;
  farmNameKz: string;
  farmNameRu: string;
  regionKz: string;
  regionRu: string;
  date: string;
  auctionType: AuctionType;
  breed: string;
  status: AuctionStatus;
  coverImage?: string | null;
  lotsCount: number;
  startsAt?: string | null;
  endsAt?: string | null;
  biddingState: LotBiddingState;
}

export interface AuctionItem {
  id: number;
  titleKz: string;
  titleRu: string;
  descriptionKz?: string | null;
  descriptionRu?: string | null;
  farmNameKz: string;
  farmNameRu: string;
  regionKz: string;
  regionRu: string;
  date: string;
  auctionType: AuctionType;
  breed: string;
  status: AuctionStatus;
  youtubeUrl?: string | null;
  youtubeEmbedUrl?: string | null;
  coverImage?: string | null;
  bannerImage?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  minBidIncrement: number;
  biddingState: LotBiddingState;
  serverTime: string;
  lots: AuctionLotItem[];
  createdAt: string;
  updatedAt?: string | null;
}

export interface CreateAuctionRequest {
  titleKz: string;
  titleRu: string;
  descriptionKz?: string | null;
  descriptionRu?: string | null;
  farmNameKz: string;
  farmNameRu: string;
  regionKz: string;
  regionRu: string;
  date: string;
  auctionType: AuctionType;
  breed: string;
  status: AuctionStatus;
  youtubeUrl?: string | null;
  youtubeEmbedUrl?: string | null;
  coverImage?: string | null;
  bannerImage?: string | null;
  /** Начало торгов (UTC ISO) */
  startsAt?: string | null;
  /** Окончание торгов (UTC ISO) */
  endsAt?: string | null;
  minBidIncrement: number;
}

export interface CreateAuctionLotRequest {
  lotNumber: number;
  name: string;
  inj: string;
  birthDate?: string | null;
  ageMonths?: number | null;
  birthWeight?: number | null;
  weaningWeight?: number | null;
  yearlingWeight?: number | null;
  currentWeight?: number | null;
  averageDailyGain?: number | null;
  scrotalCircumference?: number | null;
  spermQualityScore?: number | null;
  heightAtWithers?: number | null;
  line?: string | null;
  startingPrice: number;
  image?: string | null;
  imageUrl?: string | null;
  closesAt?: string | null;
}

export interface AuctionParticipantItem {
  id: number;
  auctionId: number;
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  status: ParticipantStatus;
  number: number;
  bidsCount: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface AuctionBidItem {
  id: number;
  lotId: number;
  amount: number;
  createdAt: string;
  bidderName: string;
  bidderNumber: number;
  isMine: boolean;
  bidderUserId?: number | null;
  bidderEmail?: string | null;
}

const BASE = API_ENDPOINTS.AUCTION;

export const auctionService = {
  getAll(status?: AuctionStatus, auctionType?: AuctionType, search?: string) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (auctionType) params.set('auctionType', auctionType);
    if (search) params.set('search', search);
    const qs = params.toString();
    return httpClient.get<AuctionListItem[]>(BASE + (qs ? `?${qs}` : ''));
  },

  getById(id: number) {
    return httpClient.get<AuctionItem>(`${BASE}/${id}`);
  },

  create(body: CreateAuctionRequest) {
    return httpClient.post<AuctionItem>(BASE, body);
  },

  update(id: number, body: CreateAuctionRequest) {
    return httpClient.put<AuctionItem>(`${BASE}/${id}`, body);
  },

  delete(id: number) {
    return httpClient.delete(`${BASE}/${id}`);
  },

  createLot(auctionId: number, body: CreateAuctionLotRequest) {
    return httpClient.post<AuctionLotItem>(`${BASE}/${auctionId}/lots`, body);
  },

  updateLot(lotId: number, body: CreateAuctionLotRequest) {
    return httpClient.put<AuctionLotItem>(`${BASE}/lots/${lotId}`, body);
  },

  deleteLot(lotId: number) {
    return httpClient.delete(`${BASE}/lots/${lotId}`);
  },

  getParticipants(auctionId: number) {
    return httpClient.get<AuctionParticipantItem[]>(`${BASE}/${auctionId}/participants`);
  },

  addParticipant(auctionId: number, email: string) {
    return httpClient.post<AuctionParticipantItem>(`${BASE}/${auctionId}/participants`, { email });
  },

  updateParticipantStatus(participantId: number, status: ParticipantStatus) {
    return httpClient.put<AuctionParticipantItem>(`${BASE}/participants/${participantId}/status`, {
      status,
    });
  },

  getLotBids(lotId: number) {
    return httpClient.get<AuctionBidItem[]>(`${BASE}/lots/${lotId}/bids`);
  },
};
